/**
 * @file `HistoryPanel` — the `/emails/history` route: the unified delivery
 *       history across one-off (individual) + campaign mail.
 * @module @labcraft/admin/ui/emails/history-panel
 *
 * A single table over the Part 10 read model (`useEmailHistory`): recipient,
 * subject, type pill, campaign link, sent-at, and latest delivery state.
 * Filters (search, delivery-state chips, type segment, campaign select)
 * narrow it client-side; a row opens a `LowerSplit` bottom pane with that
 * email's delivery timeline. Paginated. The Individual-compose deep-link
 * `?recipient=<email>` presets the search to one learner.
 */

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type JSX,
} from 'react'
import { Link, useSearchParams } from 'react-router'
import { useEmailHistory, usePagedList } from '../hooks/index.ts'
import { DeliveryPill, deliveryFor } from './delivery_pill.tsx'
import { DeliveryTimeline } from './delivery_timeline.tsx'
import { LowerSplit } from '../kernel/lower_split.tsx'
import { Pager } from '../kernel/pager.tsx'

const COLUMNS = [
  'Recipient',
  'Subject',
  'Type',
  'Campaign',
  'Sent',
  'Latest',
] as const
const STATE_FILTERS = [
  'all',
  'sent',
  'delivered',
  'clicked',
  'delayed',
  'bounced',
  'complained',
  'failed',
] as const
type StateFilter = (typeof STATE_FILTERS)[number]
type TypeFilter = 'all' | 'individual' | 'campaign'

/** `2026-05-21T12:00:00Z` → `May 21, 12:00`; null/invalid → em dash. */
function formatStamp(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Render the unified email-history route. */
export function HistoryPanel(): JSX.Element {
  const [searchParams] = useSearchParams()
  const deepLinkRecipient = searchParams.get('recipient')

  const result = useEmailHistory()
  const all = result.data?.events ?? null
  const error = result.isError
    ? result.error.message || 'Could not load email history.'
    : null

  const [q, setQ] = useState(deepLinkRecipient ?? '')
  const [state, setState] = useState<StateFilter>('all')
  const [type, setType] = useState<TypeFilter>('all')
  const [campaign, setCampaign] = useState<string>('any')
  const [sel, setSel] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  // Adopt the deep-link recipient into the search when it changes.
  useEffect(() => {
    if (deepLinkRecipient) setQ(deepLinkRecipient)
  }, [deepLinkRecipient])

  // Distinct campaigns present, for the campaign select.
  const campaignOptions = useMemo(() => {
    const seen = new Map<string, string>()
    for (const e of all ?? []) {
      if (e.type === 'campaign' && e.campaignId && !seen.has(e.campaignId)) {
        seen.set(e.campaignId, e.subject)
      }
    }
    return [...seen.entries()]
  }, [all])

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return (all ?? []).filter(e => {
      if (type !== 'all' && e.type !== type) return false
      if (campaign !== 'any' && e.campaignId !== campaign) return false
      if (state !== 'all' && deliveryFor(e).label !== state) return false
      if (
        needle !== '' &&
        !e.email.toLowerCase().includes(needle) &&
        !e.subject.toLowerCase().includes(needle)
      ) {
        return false
      }
      return true
    })
  }, [all, q, state, type, campaign])

  const { page, setPage, resetPage, pageSize, rowHeight, offset, pageCount } =
    usePagedList(scrollRef, filtered.length)

  // Reset to page 1 whenever a filter changes.
  useEffect(() => {
    resetPage()
  }, [q, state, type, campaign, resetPage])

  const paged = filtered.slice(offset, offset + pageSize)
  const selected = useMemo(
    () =>
      filtered.find(e => e.recipientId + (e.campaignId ?? '') === sel) ?? null,
    [filtered, sel],
  )

  const table = (
    <div
      className="table-scroll"
      ref={scrollRef}
      style={
        rowHeight > 0
          ? ({ '--row-h': `${rowHeight}px` } as CSSProperties)
          : undefined
      }>
      <table className="table">
        <thead>
          <tr>
            {COLUMNS.map(c => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {result.isPending && all === null && (
            <tr>
              <td className="empty-row" colSpan={COLUMNS.length}>
                // loading…
              </td>
            </tr>
          )}
          {all && filtered.length === 0 && (
            <tr>
              <td className="empty-row" colSpan={COLUMNS.length}>
                // no emails match.
              </td>
            </tr>
          )}
          {paged.map(e => {
            const key = e.recipientId + (e.campaignId ?? '')
            return (
              <tr
                key={key}
                className="is-clickable"
                aria-selected={sel === key}
                onClick={() => setSel(key)}>
                <td className="cell-email">{e.email}</td>
                <td className="cell-subject">{e.subject || '(no subject)'}</td>
                <td>
                  <span className={`tpill tpill--${e.type}`}>{e.type}</span>
                </td>
                <td className="cell-meta">
                  {e.type === 'campaign' ? (
                    <Link to={`/emails/campaigns/${e.campaignId}`}>view</Link>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="cell-meta">{formatStamp(e.sentAt)}</td>
                <td>
                  <DeliveryPill recipient={e} />
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )

  const detail = selected && (
    <div className="rcp-detail">
      <div className="rcp-detail__head">
        <span className="cell-email">{selected.email}</span>
        <span className={`tpill tpill--${selected.type}`}>{selected.type}</span>
        <DeliveryPill recipient={selected} />
      </div>
      <dl className="kv-grid">
        <dt>subject</dt>
        <dd>{selected.subject || '(no subject)'}</dd>
        <dt>sent</dt>
        <dd>{formatStamp(selected.sentAt)}</dd>
      </dl>
      <h4 className="aside-h">Delivery timeline</h4>
      <DeliveryTimeline recipient={selected} />
    </div>
  )

  return (
    <div className="panel emails-panel emails-panel--paged">
      <header className="panel-head">
        <div>
          <div className="panel-head__title-row">
            <h1 className="panel-head__title">History</h1>
            <span className="panel-head__count">
              {all ? `${filtered.length} shown` : '—'}
            </span>
          </div>
          <p className="panel-head__sub">
            // every email — one-off and campaign — with its delivery state.
          </p>
        </div>
      </header>

      {error !== null && (
        <div className="panel-banner panel-banner--error">
          <b>Could not load email history.</b> {error}
        </div>
      )}

      <div className="emails-subbar">
        <input
          className="lc-input emails-search"
          type="search"
          placeholder="search recipient or subject…"
          value={q}
          onChange={e => setQ(e.target.value)}
        />
        <div className="emails-subbar__filters">
          <div className="seg" role="group" aria-label="Filter by type">
            {(['all', 'individual', 'campaign'] as const).map(t => (
              <button
                key={t}
                type="button"
                className="seg__btn"
                aria-pressed={type === t}
                onClick={() => setType(t)}>
                {t === 'all'
                  ? 'All'
                  : t === 'individual'
                    ? 'Individual'
                    : 'Campaign'}
              </button>
            ))}
          </div>
          <select
            className="lc-input emails-campaign-select"
            value={campaign}
            aria-label="Filter by campaign"
            onChange={e => setCampaign(e.target.value)}>
            <option value="any">Any campaign</option>
            {campaignOptions.map(([id, subject]) => (
              <option key={id} value={id}>
                {subject || '(no subject)'}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div
        className="chips emails-statechips"
        role="group"
        aria-label="Filter by delivery state">
        {STATE_FILTERS.map(s => (
          <button
            key={s}
            type="button"
            className="chip"
            aria-pressed={state === s}
            onClick={() => setState(s)}>
            {s}
          </button>
        ))}
      </div>

      <LowerSplit
        open={selected !== null}
        storageKey="emails-history"
        detail={detail}>
        <div className="paged-list">
          {table}
          <div className="paged-foot">
            <Pager
              page={page}
              pageCount={pageCount}
              onPage={setPage}
              total={all ? filtered.length : undefined}
            />
          </div>
        </div>
      </LowerSplit>
    </div>
  )
}
